export const BUYER_AVAILABILITY_CONFIRMED_BADGE_LABEL = "Confirmed available";

export const DEFAULT_BUYER_AVAILABILITY_CONFIRMED_WINDOW_DAYS = 7;

export type SellerAvailabilityConfirmedInput = {
  sellerAvailabilityConfirmedAt?: Date | string | null;
};

export type SellerAvailabilityConfirmedOptions = {
  enabled?: boolean;
  windowDays?: number;
  now?: Date;
};

function toTimestamp(value: Date | string | null | undefined): number | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  return Number.isFinite(time) ? time : null;
}

export function isSellerAvailabilityConfirmed(
  item: SellerAvailabilityConfirmedInput,
  options: SellerAvailabilityConfirmedOptions = {},
): boolean {
  if (options.enabled === false) {
    return false;
  }

  const confirmedAt = toTimestamp(item.sellerAvailabilityConfirmedAt);
  if (confirmedAt == null) {
    return false;
  }

  const windowDays =
    options.windowDays != null && options.windowDays > 0
      ? options.windowDays
      : DEFAULT_BUYER_AVAILABILITY_CONFIRMED_WINDOW_DAYS;

  const now = options.now ?? new Date();
  const windowMs = windowDays * 24 * 60 * 60 * 1000;
  const ageMs = now.getTime() - confirmedAt;

  return ageMs >= 0 && ageMs <= windowMs;
}

export function getBuyerAvailabilityDetailLine(windowDays: number): string {
  const days =
    windowDays > 0
      ? windowDays
      : DEFAULT_BUYER_AVAILABILITY_CONFIRMED_WINDOW_DAYS;
  return `Seller confirmed this is still for sale within the last ${days} days.`;
}

export type AvailabilitySortBoostOptions = {
  sortBoostEnabled?: boolean;
  windowDays?: number;
  now?: Date;
};

/** 1 when confirmed within window and boost is on; otherwise 0. */
export function getAvailabilitySortBoostScore(
  item: SellerAvailabilityConfirmedInput,
  options: AvailabilitySortBoostOptions = {},
): number {
  if (options.sortBoostEnabled !== true) {
    return 0;
  }

  return isSellerAvailabilityConfirmed(item, {
    enabled: true,
    windowDays: options.windowDays,
    now: options.now,
  })
    ? 1
    : 0;
}

/**
 * Applies availability tie-break only when `primaryCompare` returns 0 (same tier peers).
 * Confirmed listings rank above unconfirmed; then newest `createdAt`, then stable id.
 */
export function compareWithinTierWithAvailabilityBoost<
  T extends SellerAvailabilityConfirmedInput & {
    createdAt: Date;
    id: string | number;
  },
>(
  a: T,
  b: T,
  primaryCompare: (left: T, right: T) => number,
  options: AvailabilitySortBoostOptions,
): number {
  const primary = primaryCompare(a, b);
  if (primary !== 0 || options.sortBoostEnabled !== true) {
    return primary;
  }

  const scoreDiff =
    getAvailabilitySortBoostScore(b, options) -
    getAvailabilitySortBoostScore(a, options);
  if (scoreDiff !== 0) {
    return scoreDiff;
  }

  const createdDiff = b.createdAt.getTime() - a.createdAt.getTime();
  if (createdDiff !== 0) {
    return createdDiff;
  }

  return String(a.id).localeCompare(String(b.id));
}
